import { useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react"

import { useApplicationScope } from "../application/application-provider"
import { ApplicationScopeContext } from "../application/application-context"
import { useDocumentInstance } from "../application/document-instance"
import {
  dataDependencyEquals,
  dataInvalidationAffects,
  normalizeDataDependencies,
  type DataDependency,
  type DataDependencyInput,
  type DataInvalidation,
  type DataInvalidationEvent,
} from "../data/data-invalidation"
import {
  createRemoteCollection,
  createRemoteCollectionHandle,
  remoteCollectionHasPageError,
  snapshotCollectionQuery,
  type RemoteCollection,
  type RemoteCollectionHandle,
  type RemoteCollectionQueryConstraint,
  type RemoteCollectionOptions,
  type RemoteCollectionCompleteOptions,
  type RemoteCollectionPagingOptions,
  type RemoteCollectionSorting,
} from "../data/remote-collection"
import {
  createGraphQLCollection,
  type GraphQLCollectionOptions,
  type GraphQLConnectionQueryDefinition,
  type GraphQLConnectionQueryInstance,
} from "./graphql-connection-query"
import type { GraphQLQueryDefinition, GraphQLQueryInstance } from "./graphql-query"
import {
  getRemoteRevision,
  isRemoteAuthenticationFailure,
  subscribeRemoteAuthorizationResumed,
  subscribeRemoteRevision,
  type RemoteClient,
} from "./remote-client"

interface RemoteQueryBehaviorOptions<TData> {
  enabled?: boolean
  /** Observed-query polling. Returning false stops polling without a timer service. */
  refetchInterval?: number | false | ((data: TData | undefined) => number | false)
  refetchIntervalInBackground?: boolean
  /** Opt-in revalidation when the browser tab becomes visible again. */
  refetchOnWindowFocus?: boolean
  /** Opt-in revalidation when the browser reports that network connectivity returned. */
  refetchOnReconnect?: boolean
}

type RemoteQueryParameters<TParameters> = [TParameters] extends [void]
  ? { readonly parameters?: never }
  : Record<never, never> extends TParameters
    ? { readonly parameters?: TParameters }
    : { readonly parameters: TParameters }

export type RemoteQueryOptions<TData = unknown, TParameters = void> = RemoteQueryBehaviorOptions<TData> &
  RemoteQueryParameters<TParameters>

export interface RemoteQueryState<TData> {
  readonly data: TData | undefined
  readonly error: unknown
  readonly status: "pending" | "error" | "success"
  readonly isPending: boolean
  readonly isFetching: boolean
  readonly isRefetching: boolean
  readonly isError: boolean
  readonly isSuccess: boolean
  refetch(): Promise<void>
  /** Cancels only this hook's current request. */
  cancel(): Promise<void>
}

interface RemoteQuerySnapshot<TData> {
  readonly data: TData | undefined
  readonly error: unknown
  readonly status: "pending" | "error" | "success"
  readonly isFetching: boolean
}

interface RemoteQueryExecution {
  readonly controller: AbortController
  promise: Promise<void>
}

export function useRemoteQuery<TData, TParameters, TVariables extends object>(
  definition: GraphQLQueryDefinition<TData, TParameters, TVariables>,
  ...args: [TParameters] extends [void]
    ? [options?: RemoteQueryOptions<TData, TParameters>]
    : Record<never, never> extends TParameters
      ? [options?: RemoteQueryOptions<TData, TParameters>]
      : [options: RemoteQueryOptions<TData, TParameters>]
): RemoteQueryState<TData> {
  const scope = useApplicationScope()
  const documentInstance = useDocumentInstance()
  const options = (args[0] ?? {}) as RemoteQueryBehaviorOptions<TData> & { readonly parameters?: TParameters }
  const query = useStableQueryInstance(createObservedQuery(definition, options))
  const dependencies = useStableDataDependencies(definition.dependencies(options.parameters as TParameters))
  const dataRevision = useDataRevision(scope.dataInvalidation, dependencies)
  const enabled = (options.enabled ?? true) && (documentInstance?.active ?? true)
  const browserVisible = useBrowserVisibility()
  const browserOnline = useBrowserOnline()
  const previousBrowserVisibleRef = useRef(browserVisible)
  const previousBrowserOnlineRef = useRef(browserOnline)
  const intervalRef = useRef(options.refetchInterval)
  intervalRef.current = options.refetchInterval
  const intervalMode = typeof options.refetchInterval === "function" ? "function" : options.refetchInterval
  const remoteRevision = useSyncExternalStore(
    (listener) => subscribeRemoteRevision(scope.remote, listener),
    () => getRemoteRevision(scope.remote),
    () => getRemoteRevision(scope.remote)
  )
  const [snapshot, setSnapshot] = useState<RemoteQuerySnapshot<TData>>(() => pendingQuerySnapshot())
  const snapshotRef = useRef(snapshot)
  snapshotRef.current = snapshot
  const executionRef = useRef<RemoteQueryExecution | null>(null)
  const identityRef = useRef({ query, remoteRevision })

  const startExecution = useCallback(
    (reset: boolean) => {
      const previous = executionRef.current
      if (previous != null) {
        executionRef.current = null
        previous.controller.abort()
      }

      const controller = new AbortController()
      const execution: RemoteQueryExecution = { controller, promise: Promise.resolve() }
      executionRef.current = execution
      setSnapshot((current) =>
        reset
          ? { ...pendingQuerySnapshot<TData>(), isFetching: true }
          : {
              ...current,
              error: undefined,
              status: current.data === undefined ? "pending" : "success",
              isFetching: true,
            }
      )

      execution.promise = (async () => {
        try {
          const data = await query.execute(scope.remote, { signal: controller.signal })
          if (controller.signal.aborted || executionRef.current !== execution) return
          setSnapshot({ data, error: undefined, status: "success", isFetching: false })
        } catch (error) {
          if (controller.signal.aborted || executionRef.current !== execution) return
          setSnapshot((current) => ({ ...current, error, status: "error", isFetching: false }))
        } finally {
          if (executionRef.current === execution) executionRef.current = null
        }
      })()

      return execution.promise
    },
    [query, remoteRevision, scope.remote]
  )

  const cancelExecution = useCallback(async (updateState: boolean) => {
    const execution = executionRef.current
    if (execution == null) {
      if (updateState) setSnapshot((current) => ({ ...current, isFetching: false }))
      return
    }
    executionRef.current = null
    execution.controller.abort()
    if (updateState) setSnapshot((current) => ({ ...current, isFetching: false }))
    await execution.promise
  }, [])

  useEffect(() => {
    const previousIdentity = identityRef.current
    const identityChanged = previousIdentity.query !== query || previousIdentity.remoteRevision !== remoteRevision
    identityRef.current = { query, remoteRevision }
    let disposed = false

    if (identityChanged) setSnapshot(pendingQuerySnapshot())
    if (!enabled) {
      void cancelExecution(true)
      return
    }

    // React StrictMode disconnects the first effect synchronously. Deferring the
    // request prevents a throwaway development-only HTTP call.
    void Promise.resolve().then(() => {
      if (!disposed) void startExecution(identityChanged)
    })

    return () => {
      disposed = true
      void cancelExecution(false)
    }
  }, [cancelExecution, dataRevision, enabled, query, remoteRevision, startExecution])

  useEffect(() => {
    if (!enabled || snapshot.isFetching || intervalMode == null || intervalMode === false) return
    if (options.refetchIntervalInBackground !== true && !browserVisible) return

    const interval = intervalRef.current
    const delay = typeof interval === "function" ? interval(snapshot.data) : interval
    if (delay === false || delay == null || !Number.isFinite(delay) || delay < 0) return
    const timer = globalThis.setTimeout(() => {
      void startExecution(false)
    }, delay)
    return () => globalThis.clearTimeout(timer)
  }, [
    browserVisible,
    enabled,
    intervalMode,
    options.refetchIntervalInBackground,
    snapshot.data,
    snapshot.isFetching,
    startExecution,
  ])

  useEffect(() => {
    if (!enabled) return
    return subscribeRemoteAuthorizationResumed(scope.remote, () => {
      const current = snapshotRef.current
      // A request in flight was sent with the expired authorization, or none, and is restarted as well.
      if (
        executionRef.current != null ||
        (current.status === "error" && isRemoteAuthenticationFailure(current.error))
      ) {
        void startExecution(false)
      }
    })
  }, [enabled, scope.remote, startExecution])

  useEffect(() => {
    const wasVisible = previousBrowserVisibleRef.current
    previousBrowserVisibleRef.current = browserVisible
    if (!enabled || options.refetchOnWindowFocus !== true || wasVisible || !browserVisible) return
    void startExecution(false)
  }, [browserVisible, enabled, options.refetchOnWindowFocus, startExecution])

  useEffect(() => {
    const wasOnline = previousBrowserOnlineRef.current
    previousBrowserOnlineRef.current = browserOnline
    if (!enabled || options.refetchOnReconnect !== true || wasOnline || !browserOnline) return
    void startExecution(false)
  }, [browserOnline, enabled, options.refetchOnReconnect, startExecution])

  return {
    data: snapshot.data,
    error: snapshot.error,
    status: snapshot.status,
    isPending: snapshot.status === "pending",
    isFetching: snapshot.isFetching,
    isRefetching: snapshot.isFetching && snapshot.status !== "pending",
    isError: snapshot.status === "error",
    isSuccess: snapshot.status === "success",
    refetch: () => startExecution(false),
    cancel: () => cancelExecution(true),
  }
}

function createObservedQuery<TData, TParameters, TVariables extends object>(
  definition: GraphQLQueryDefinition<TData, TParameters, TVariables>,
  options: { readonly parameters?: TParameters }
) {
  const create = definition.create.bind(definition) as (
    parameters?: TParameters
  ) => GraphQLQueryInstance<TData, TVariables>
  return Object.prototype.hasOwnProperty.call(options, "parameters") ? create(options.parameters) : create()
}

function useStableQueryInstance<TData, TVariables extends object>(candidate: GraphQLQueryInstance<TData, TVariables>) {
  const current = useRef(candidate)
  if (!sameQuery(current.current, candidate)) current.current = candidate
  return current.current
}

function sameQuery<TData, TVariables extends object>(
  left: GraphQLQueryInstance<TData, TVariables>,
  right: GraphQLQueryInstance<TData, TVariables>
) {
  return (
    left.endpoint === right.endpoint && left.document === right.document && deepEqual(left.variables, right.variables)
  )
}

function deepEqual(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) return true
  if (left == null || right == null || typeof left !== "object" || typeof right !== "object") return false
  if (Array.isArray(left) || Array.isArray(right)) {
    if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) return false
    return left.every((value, index) => deepEqual(value, right[index]))
  }
  const leftRecord = left as Record<string, unknown>
  const rightRecord = right as Record<string, unknown>
  const leftKeys = Object.keys(leftRecord)
  const rightKeys = Object.keys(rightRecord)
  if (leftKeys.length !== rightKeys.length) return false
  return leftKeys.every(
    (key) => Object.prototype.hasOwnProperty.call(rightRecord, key) && deepEqual(leftRecord[key], rightRecord[key])
  )
}

function useStableValue<T>(candidate: T): T {
  const current = useRef(candidate)
  if (!deepEqual(current.current, candidate)) current.current = candidate
  return current.current
}

function useStableDataDependencies(candidate: readonly DataDependency[]) {
  const current = useRef<readonly DataDependency[]>(candidate)
  if (
    current.current.length !== candidate.length ||
    current.current.some((dependency, index) => !dataDependencyEquals(dependency, candidate[index]!))
  ) {
    current.current = Object.freeze(candidate.slice())
  }
  return current.current
}

function useDataRevision(dataInvalidation: DataInvalidation | undefined, dependencies: readonly DataDependency[]) {
  const subscribe = useCallback(
    (listener: () => void) => dataInvalidation?.subscribe(() => listener()) ?? (() => {}),
    [dataInvalidation]
  )
  const getSnapshot = useCallback(() => dataInvalidation?.revision(dependencies) ?? 0, [dataInvalidation, dependencies])
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

function pendingQuerySnapshot<TData>(): RemoteQuerySnapshot<TData> {
  return { data: undefined, error: undefined, status: "pending", isFetching: false }
}

function useBrowserVisibility() {
  return useSyncExternalStore(
    (listener) => {
      if (typeof document === "undefined") return () => undefined
      document.addEventListener("visibilitychange", listener)
      return () => document.removeEventListener("visibilitychange", listener)
    },
    () => typeof document === "undefined" || document.visibilityState !== "hidden",
    () => true
  )
}

function useBrowserOnline() {
  return useSyncExternalStore(
    (listener) => {
      if (typeof window === "undefined") return () => undefined
      window.addEventListener("online", listener)
      window.addEventListener("offline", listener)
      return () => {
        window.removeEventListener("online", listener)
        window.removeEventListener("offline", listener)
      }
    },
    () => typeof navigator === "undefined" || navigator.onLine !== false,
    () => true
  )
}

interface RemoteCollectionBehaviorOptions {
  enabled?: boolean
  refetchOnWindowFocus?: boolean
  refetchOnReconnect?: boolean
}

export type RemoteCollectionHookOptions<TItem, TQuery, TSortField extends string = string> = RemoteCollectionOptions<
  TItem,
  TQuery,
  TSortField
> &
  RemoteCollectionBehaviorOptions &
  RemoteCollectionQueryConstraint<NoInfer<TQuery>> & {
    /** Override application-scoped invalidation, or supply it for standalone hooks. */
    dataInvalidation?: DataInvalidation
    consistency?: {
      dependsOn: DataDependencyInput | ((query: Readonly<TQuery>) => DataDependencyInput)
      itemDependsOn?: (item: Readonly<TItem>) => DataDependencyInput
    }
  }

/** A custom backend needs only its typed fetch function, query and stable key. */
export function useRemoteCollection<TItem, TQuery, TSortField extends string = string>(
  options: RemoteCollectionHookOptions<TItem, TQuery, TSortField>
): RemoteCollectionHandle<TItem, TQuery, TSortField> {
  const scope = useContext(ApplicationScopeContext)
  const dataInvalidation = options.dataInvalidation ?? scope?.dataInvalidation
  const documentInstance = useDocumentInstance()
  const appliedState = useRef({
    query: snapshotCollectionQuery(options.initialQuery),
    sorting: options.initialSorting,
    selectedKeys: [] as readonly string[],
  })
  const callbacks = useRef({ fetchPage: options.fetchPage, getKey: options.getKey })
  callbacks.current = { fetchPage: options.fetchPage, getKey: options.getKey }
  const paging = useStableValue({
    pageSize: options.pageSize,
    includeTotalCount: options.includeTotalCount,
    prefetch: options.prefetch,
    maxConcurrentRequests: options.maxConcurrentRequests,
    sortableFields: options.sortableFields,
    maxSortFields: options.maxSortFields,
    selectionOnQueryChange: options.selectionOnQueryChange,
    complete: options.complete,
  })
  const collection = useMemo(() => {
    const next = createRemoteCollection<TItem, TQuery, TSortField>({
      ...paging,
      initialQuery: appliedState.current.query,
      initialSorting: appliedState.current.sorting,
      fetchPage: (request) => callbacks.current.fetchPage(request),
      getKey: (item) => callbacks.current.getKey(item),
    } as RemoteCollectionOptions<TItem, TQuery, TSortField> & RemoteCollectionQueryConstraint<NoInfer<TQuery>>)
    next.setSelectedKeys(appliedState.current.selectedKeys)
    return next
  }, [paging])
  const subscribe = useCallback((listener: () => void) => collection.subscribe(listener), [collection])
  const snapshot = useCallback(() => collection.revision, [collection])
  useSyncExternalStore(subscribe, snapshot, snapshot)
  appliedState.current = {
    query: collection.query as TQuery,
    sorting: collection.sorting,
    selectedKeys: collection.selectedKeys,
  }
  const enabled = (options.enabled ?? true) && (documentInstance?.active ?? true)
  useCollectionActivity(collection, enabled)
  useCollectionRefetch(collection, enabled, options)
  useCollectionAuthenticationResume(collection, enabled, scope?.remote)
  if (options.consistency && !dataInvalidation)
    throw new Error("Remote collection consistency requires dataInvalidation or an ApplicationProvider.")
  const consistency = useMemo(
    () =>
      options.consistency
        ? {
            collection: normalizeDataDependencies(
              typeof options.consistency.dependsOn === "function"
                ? options.consistency.dependsOn(collection.query)
                : options.consistency.dependsOn
            ),
            item: options.consistency.itemDependsOn
              ? (item: Readonly<TItem>) => normalizeDataDependencies(options.consistency!.itemDependsOn!(item))
              : undefined,
          }
        : undefined,
    [collection.query, options.consistency]
  )
  useCollectionConsistency(collection, enabled, dataInvalidation, consistency)
  return useMemo(() => createRemoteCollectionHandle(collection), [collection])
}

export function useGraphQLCollection<
  TData,
  TItem,
  TParameters,
  TVariables extends object,
  TSortField extends string,
  TBackendField extends string,
>(
  definition: GraphQLConnectionQueryDefinition<TData, TItem, TParameters, TVariables, TSortField, TBackendField>,
  collectionOptions: GraphQLCollectionOptions<NoInfer<TItem>, NoInfer<TParameters>, NoInfer<TSortField>> &
    RemoteCollectionBehaviorOptions
): RemoteCollectionHandle<TItem, TParameters, TSortField> {
  const scope = useApplicationScope()
  const documentInstance = useDocumentInstance()
  const options = collectionOptions as RemoteCollectionPagingOptions &
    RemoteCollectionBehaviorOptions & {
      readonly initialQuery?: TParameters
      getKey: (item: TItem) => string | number
      sortableFields?: readonly TSortField[]
      initialSorting?: RemoteCollectionSorting<TSortField>
      maxSortFields?: number
      selectionOnQueryChange?: "clear" | "preserve"
      complete?: RemoteCollectionCompleteOptions
    }
  const hasParameters = Object.prototype.hasOwnProperty.call(options, "initialQuery")
  const getKeyRef = useRef(options.getKey)
  getKeyRef.current = options.getKey
  const appliedState = useRef({
    query: snapshotCollectionQuery(options.initialQuery),
    sorting: options.initialSorting,
    selectedKeys: [] as readonly string[],
    remoteRevision: -1,
    definition,
    remote: scope.remote,
  })
  const remoteRevision = useSyncExternalStore(
    (listener) => subscribeRemoteRevision(scope.remote, listener),
    () => getRemoteRevision(scope.remote),
    () => getRemoteRevision(scope.remote)
  )
  const listOptions = useStableValue({
    pageSize: options.pageSize,
    includeTotalCount: options.includeTotalCount,
    prefetch: options.prefetch == null ? undefined : { ahead: options.prefetch.ahead, behind: options.prefetch.behind },
    maxConcurrentRequests: options.maxConcurrentRequests,
    sortableFields: options.sortableFields,
    maxSortFields: options.maxSortFields,
    selectionOnQueryChange: options.selectionOnQueryChange,
    complete: options.complete,
  })
  const list = useMemo(() => {
    // Sorting kept from another definition keeps only the fields this one sorts on.
    const sortable = listOptions.sortableFields ?? (Object.keys(definition.sorting) as TSortField[])
    const initialSorting = appliedState.current.sorting
      ?.filter((sort) => sortable.includes(sort.id))
      .slice(0, listOptions.maxSortFields ?? definition.maxSortFields)
    const next = createGraphQLCollection(definition, scope.remote, {
      ...listOptions,
      ...(hasParameters ? { initialQuery: appliedState.current.query } : {}),
      initialSorting,
      getKey: (item: TItem) => getKeyRef.current(item),
    } as GraphQLCollectionOptions<TItem, TParameters, TSortField>)
    const previous = appliedState.current
    if (
      previous.remoteRevision === remoteRevision &&
      previous.definition === definition &&
      previous.remote === scope.remote
    ) {
      next.setSelectedKeys(previous.selectedKeys)
    }
    return next
  }, [definition, hasParameters, listOptions, remoteRevision, scope.remote])
  const subscribeList = useCallback((listener: () => void) => list.subscribe(listener), [list])
  const getListRevision = useCallback(() => list.revision, [list])
  useSyncExternalStore(subscribeList, getListRevision, getListRevision)
  appliedState.current = {
    query: list.query as TParameters,
    sorting: list.sorting,
    selectedKeys: list.selectedKeys,
    remoteRevision,
    definition,
    remote: scope.remote,
  }
  const query = useMemo(
    () => createObservedConnectionQuery(definition, list.query as TParameters, hasParameters),
    [definition, hasParameters, list.query]
  )

  const enabled = (options.enabled ?? true) && (documentInstance?.active ?? true)
  useCollectionActivity(list, enabled)
  useCollectionRefetch(list, enabled, options)
  useCollectionAuthenticationResume(list, enabled, scope.remote)
  useCollectionConsistency(list, enabled, scope.dataInvalidation, query.consistency)
  return useMemo(() => createRemoteCollectionHandle(list), [list])
}

function useCollectionConsistency<TItem, TQuery, TSortField extends string>(
  list: RemoteCollection<TItem, TQuery, TSortField>,
  enabled: boolean,
  dataInvalidation: DataInvalidation | undefined,
  consistency:
    { collection: readonly DataDependency[]; item?: (item: Readonly<TItem>) => readonly DataDependency[] } | undefined
) {
  const enabledRef = useRef(enabled)
  enabledRef.current = enabled
  const collectionDependencies = useStableDataDependencies(consistency?.collection ?? [])
  const collectionRevision = useDataRevision(dataInvalidation, collectionDependencies)
  const collectionStateRef = useRef({ list, revision: collectionRevision })
  const pendingCollectionRefreshRef = useRef(false)
  const pendingItemInvalidationsRef = useRef<DataDependency[]>([])

  useEffect(() => {
    const state = collectionStateRef.current
    if (state.list !== list) {
      collectionStateRef.current = { list, revision: collectionRevision }
      pendingCollectionRefreshRef.current = false
      pendingItemInvalidationsRef.current = []
      return
    }
    if (state.revision !== collectionRevision) {
      state.revision = collectionRevision
      pendingCollectionRefreshRef.current = true
    }
    if (!enabled || !pendingCollectionRefreshRef.current) return

    pendingCollectionRefreshRef.current = false
    pendingItemInvalidationsRef.current = []
    void list.refresh().catch(() => {
      // PagedList retains the page error for the consumer.
    })
  }, [collectionRevision, enabled, list])

  useEffect(() => {
    const itemDependencies = consistency?.item
    if (itemDependencies == null) return
    return dataInvalidation?.subscribe((event) => {
      if (eventAffectsAny(collectionDependencies, event)) return
      if (!enabledRef.current) {
        mergeInvalidations(pendingItemInvalidationsRef.current, event.dependencies)
        return
      }
      void refreshPagedListItems(list, itemDependencies, event.dependencies)
    })
  }, [collectionDependencies, list, consistency?.item, dataInvalidation])

  useEffect(() => {
    const itemDependencies = consistency?.item
    if (!enabled || itemDependencies == null || pendingCollectionRefreshRef.current) return
    const pending = pendingItemInvalidationsRef.current
    if (pending.length === 0) return
    pendingItemInvalidationsRef.current = []
    void refreshPagedListItems(list, itemDependencies, pending)
  }, [enabled, list, consistency?.item])
}

function useCollectionActivity<TItem, TQuery, TSortField extends string>(
  collection: RemoteCollection<TItem, TQuery, TSortField>,
  enabled: boolean
) {
  const lifetime = useRef<{ collection: RemoteCollection<TItem, TQuery, TSortField>; token: object } | undefined>(
    undefined
  )
  useEffect(() => {
    const token = {}
    lifetime.current = { collection, token }
    collection.setActive(enabled)
    return () => {
      collection.setActive(false)
      // Effect replay acquires the same collection before this microtask. A
      // replacement or final unmount releases cache and outstanding resources.
      queueMicrotask(() => {
        if (lifetime.current?.collection !== collection || lifetime.current.token === token) collection.dispose()
      })
    }
  }, [collection, enabled])
}

function useCollectionRefetch<TItem, TQuery, TSortField extends string>(
  collection: RemoteCollection<TItem, TQuery, TSortField>,
  enabled: boolean,
  options: RemoteCollectionBehaviorOptions
) {
  const visible = useBrowserVisibility()
  const online = useBrowserOnline()
  const previous = useRef({ visible, online })
  useEffect(() => {
    const was = previous.current
    previous.current = { visible, online }
    if (
      enabled &&
      ((options.refetchOnWindowFocus && !was.visible && visible) ||
        (options.refetchOnReconnect && !was.online && online))
    )
      void collection.refresh().catch(() => {})
  }, [collection, enabled, online, visible, options.refetchOnWindowFocus, options.refetchOnReconnect])
}

/** Refreshes pages that failed with 401, or were loading, once the same user's login resumes the access context. */
function useCollectionAuthenticationResume<TItem, TQuery, TSortField extends string>(
  collection: RemoteCollection<TItem, TQuery, TSortField>,
  enabled: boolean,
  remote: RemoteClient | undefined
) {
  useEffect(() => {
    if (!enabled || remote == null) return
    return subscribeRemoteAuthorizationResumed(remote, () => {
      if (collection.isLoading || remoteCollectionHasPageError(collection, isRemoteAuthenticationFailure)) {
        void collection.refresh().catch(() => {
          // PagedList retains the page error for the consumer.
        })
      }
    })
  }, [collection, enabled, remote])
}

function createObservedConnectionQuery<
  TData,
  TItem,
  TParameters,
  TVariables extends object,
  TSortField extends string,
  TBackendField extends string,
>(
  definition: GraphQLConnectionQueryDefinition<TData, TItem, TParameters, TVariables, TSortField, TBackendField>,
  parameters: TParameters | undefined,
  hasParameters: boolean
) {
  const create = definition.create.bind(definition) as (
    parameters?: TParameters
  ) => GraphQLConnectionQueryInstance<TData, TItem, TVariables, TSortField, TBackendField>
  return hasParameters ? create(parameters) : create()
}

function eventAffectsAny(dependencies: readonly DataDependency[], event: DataInvalidationEvent) {
  return dependencies.some((dependency) =>
    event.dependencies.some((invalidated) => dataInvalidationAffects(dependency, invalidated))
  )
}

function mergeInvalidations(target: DataDependency[], invalidations: readonly DataDependency[]) {
  for (const invalidation of invalidations) {
    if (!target.some((current) => dataDependencyEquals(current, invalidation))) target.push(invalidation)
  }
}

async function refreshPagedListItems<TItem>(
  list: RemoteCollection<TItem, unknown>,
  itemDependencies: (item: Readonly<TItem>) => readonly DataDependency[],
  invalidations: readonly DataDependency[]
) {
  await list
    .refreshWhere((item) =>
      itemDependencies(item).some((dependency) =>
        invalidations.some((invalidated) => dataInvalidationAffects(dependency, invalidated))
      )
    )
    .catch(() => {
      // PagedList retains the page error for the consumer.
    })
}
